/**
 * Serviço de API com suporte a autenticação por Token e CSRF
 * 
 * O token de API é carregado de:
 * 1. config.json (produção - gerado em runtime pelo docker-entrypoint.sh)
 * 2. Variável de ambiente REACT_APP_API_TOKEN (desenvolvimento)
 * 
 * Para atualizar o token sem redeploy, atualize o AWS Secrets Manager
 * e reinicie os containers ECS (ou force new deployment).
 */

import { API_ENDPOINTS } from './api';
import { getConfig, loadRuntimeConfig, RuntimeConfig } from './runtimeConfig';

class ApiService {
  private csrfToken: string | null = null;
  private config: RuntimeConfig | null = null;
  private configPromise: Promise<RuntimeConfig> | null = null;

  /**
   * Carrega a configuração de runtime (token, URL base)
   */
  private async getConfigCached(): Promise<RuntimeConfig> {
    if (this.config) {
      return this.config;
    }
    
    if (!this.configPromise) {
      this.configPromise = loadRuntimeConfig().then(config => {
        this.config = config;
        return config;
      });
    }
    
    return this.configPromise;
  }

  /**
   * Retorna os headers de autenticação
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const config = await this.getConfigCached();
    const headers: Record<string, string> = {};
    
    if (config.apiToken) {
      headers['Authorization'] = `Token ${config.apiToken}`;
    }
    
    return headers;
  }

  /**
   * Retorna a URL base da API
   */
  async getApiBase(): Promise<string> {
    const config = await this.getConfigCached();
    return config.apiUrl.replace(/\/$/, '');
  }

  /**
   * Obtém o CSRF token do backend
   */
  async getCSRFToken(): Promise<string> {
    if (this.csrfToken) {
      return this.csrfToken;
    }

    try {
      const apiBase = await this.getApiBase();
      const authHeaders = await this.getAuthHeaders();
      
      const response = await fetch(`${apiBase}/api/csrf-token/`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
      });

      if (response.ok) {
        const data = await response.json();
        this.csrfToken = data.csrfToken;
        return this.csrfToken!;
      } else {
        throw new Error('Falha ao obter CSRF token');
      }
    } catch (error) {
      console.error('Erro ao obter CSRF token:', error);
      throw error;
    }
  }

  /**
   * Faz uma requisição POST com autenticação e CSRF token
   */
  async postWithCSRF(url: string, data: FormData | object, isFormData = false): Promise<Response> {
    const csrfToken = await this.getCSRFToken();
    const authHeaders = await this.getAuthHeaders();
    
    const headers: Record<string, string> = {
      'X-CSRFToken': csrfToken,
      ...authHeaders,
    };

    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const body = isFormData ? data as FormData : JSON.stringify(data);

    return fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body,
    });
  }

  /**
   * Faz uma requisição POST simples com autenticação (sem CSRF)
   */
  async post(url: string, data: FormData | object, isFormData = false): Promise<Response> {
    const authHeaders = await this.getAuthHeaders();
    
    const headers: Record<string, string> = {
      ...authHeaders,
    };

    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const body = isFormData ? data as FormData : JSON.stringify(data);

    return fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body,
    });
  }

  /**
   * Faz uma requisição PUT com autenticação e CSRF token
   */
  async putWithCSRF(url: string, data: FormData | object, isFormData = false): Promise<Response> {
    const csrfToken = await this.getCSRFToken();
    const authHeaders = await this.getAuthHeaders();
    
    const headers: Record<string, string> = {
      'X-CSRFToken': csrfToken,
      ...authHeaders,
    };

    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const body = isFormData ? data as FormData : JSON.stringify(data);

    return fetch(url, {
      method: 'PUT',
      headers,
      credentials: 'include',
      body,
    });
  }

  /**
   * Faz uma requisição DELETE com autenticação e CSRF token
   */
  async deleteWithCSRF(url: string): Promise<Response> {
    const csrfToken = await this.getCSRFToken();
    const authHeaders = await this.getAuthHeaders();
    
    return fetch(url, {
      method: 'DELETE',
      headers: {
        'X-CSRFToken': csrfToken,
        'Content-Type': 'application/json',
        ...authHeaders,
      },
      credentials: 'include',
    });
  }

  /**
   * Faz uma requisição GET com autenticação
   */
  async get(url: string): Promise<Response> {
    const authHeaders = await this.getAuthHeaders();
    
    return fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
    });
  }

  /**
   * Limpa o token CSRF armazenado
   */
  clearCSRFToken(): void {
    this.csrfToken = null;
  }

  /**
   * Limpa todo o cache (config + CSRF)
   */
  clearCache(): void {
    this.csrfToken = null;
    this.config = null;
    this.configPromise = null;
  }
}

// Exportar uma instância singleton
export const apiService = new ApiService();
